import { MarketHistoriesResponse, MarketResponse } from '../ao-data-interfaces';
import { Item } from '../items.model';
import { Achievement } from '../achievement.model';
import { findMatchingAchievements, getFocusEfficiency, getFocusEfficiencyText } from '../../functions/achievements-functions';
import { getLocation, LOCATIONS } from '../../constants/locations.constant';
import { FAME_BONUS_PREMIUM, getPriceTypeByName, PRICE_TYPES, SALES_TAXES, SETUP_FEES } from '../../constants/market.constant';
import { Location } from '../location.model';
import { PriceType } from '../market.model';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';

// MARK: FarmPlot
export class FarmPlot {
  // MARK: ins outs
  plotInput: PlotInputItem;
  nutritionInputs: PlotNutritionItem[] = [];
  outputs: PlotOuputItem[] = [];
  get hasNoSelectedNutrition(): boolean {
    if (this.nutritionInputs.length === 0) return false;
    return this.nutritionInputs.every((x) => !x.isSelected);
  }

  // MARK: props
  kind?: string;
  achievements: Achievement[] = [];

  // MARK: fame
  get plotFame(): number {
    if (this.userSettings.getUserFarmConfig().hasPremium) return this.baseFame * (1 + FAME_BONUS_PREMIUM);
    return this.baseFame;
  }

  get famePerDay(): number {
    return this.plotFame / (this.realFarmDurationSeconds / 86400);
  }

  get famePerFocus(): number {
    if (this.actionMaxFocusCost === 0) return 0;
    return this.plotFame / this.actionMaxFocusCost;
  }

  // MARK: focus
  actionBaseFocusCost?: number;
  actionCycleLengthSeconds?: number;
  actionCycleBonus?: number;
  maxActionCycles?: number;
  get focusEfficiency(): number {
    return getFocusEfficiency(this.achievements, this.plotInput.item, 'farmingfocuscostreduction');
  }

  get actionFocusCost(): number {
    return (this.actionBaseFocusCost ?? 0) * Math.pow(0.5, (this.focusEfficiency ?? 0) / 10000);
  }

  get actionMaxFocusCost(): number {
    return this.actionFocusCost * (this.maxActionCycles ?? 0);
  }

  get actionMaxBonus(): number {
    return (this.actionCycleBonus ?? 0) * (this.maxActionCycles ?? 0);
  }

  // MARK: duration
  get farmDurationSecondsNoPremium(): number {
    return this.plotInput.item.farmHarvest
      ? this.plotInput.item.farmHarvest.growTime
      : this.plotInput.item.farmGrownItem
        ? this.plotInput.item.farmGrownItem.growTime
        : this.plotInput.item.farmProducts
          ? this.plotInput.item.farmProducts.product.productionTime
          : 0;
  }

  get farmDurationSecondsPremium(): number {
    return this.farmDurationSecondsNoPremium / 2;
  }

  get realFarmDurationSeconds(): number {
    if (!this.plotInput.item.farmGrownItem) return this.farmDurationSecondsNoPremium;
    return this.userSettings.getUserFarmConfig().hasPremium ? this.farmDurationSecondsPremium : this.farmDurationSecondsNoPremium;
  }

  //MARK: expenses
  get expenses(): number {
    let result = 0;
    result += this.plotInput.feesCost + this.plotInput.totalItemValue;
    const selectedNutrition = this.nutritionInputs.find((x) => x.isSelected);
    result += selectedNutrition ? selectedNutrition.feesCost + selectedNutrition.totalItemValue : 0;
    result += this.outputs.reduce((acc, x) => acc + x.feesCost + x.salesTaxesCost, 0);
    return result;
  }

  // MARK: income
  get income(): number {
    let result = 0;
    this.outputs.forEach((output) => {
      result += output.totalItemValue;
    });
    return result;
  }

  // MARK: profit
  get profit(): number {
    return this.income - this.expenses;
  }

  get profitPerDay(): number {
    return this.profit / (this.realFarmDurationSeconds / 86400);
  }

  get profitPerFocus(): number {
    if (this.actionMaxFocusCost === 0) return 0;
    return this.profit / this.actionMaxFocusCost;
  }

  // MARK: prices
  get hasMissingPrices(): boolean {
    if (this.plotInput.chosenPrice === 0) return true;

    if (this.outputs.find((x) => x.chosenPrice === 0)) return true;

    if (this.nutritionInputs.find((x) => (x.isFavorite || x.isSelected) && x.chosenPrice === 0)) return true;

    return false;
  }

  // MARK: constructor
  constructor(
    baseInput: Item,
    achievements: Achievement[],
    private userSettings: UserSettingsService,
    foodItems: Item[],
    private aodpPrices: MarketResponse[],
    private aodpHistories: MarketHistoriesResponse[],
    private aoDataGetter: AoDataGetterService,
    private farmingModifiers: FarmingModifier[],
    public baseFame: number
  ) {
    this.plotInput = new PlotInputItem(baseInput, baseInput.farmProducts ? 0 : 1, this.userSettings, aodpPrices, aodpHistories, aoDataGetter);

    // Props
    this.kind = baseInput.farmKind;
    this.maxActionCycles = baseInput.farmActiveFarmMaxCycles;
    this.actionCycleLengthSeconds = baseInput.farmActiveFarmCycleLengthSeconds;
    this.actionCycleBonus = baseInput.farmActiveFarmBonus;
    this.actionBaseFocusCost = baseInput.farmActiveFarmFocusCost;

    // If farmHarvest
    if (baseInput.farmHarvest) {
      baseInput.farmHarvest.lootList.lootItems.forEach((loot, index) => {
        if (loot && loot.item) {
          const newOutput = new PlotOuputItem(
            loot.item,
            this.userSettings,
            aodpPrices,
            aodpHistories,
            aoDataGetter,
            { min: loot.amountMin, max: loot.amountMax },
            loot.chance,
            0,
            1,
            this.farmingModifiers,
            this.plotInput.item.uniqueName
          );
          this.outputs.push(newOutput);
        }
      });

      const newSeedOutput = new PlotOuputItem(
        baseInput,
        this.userSettings,
        aodpPrices,
        aodpHistories,
        aoDataGetter,
        { min: baseInput.farmHarvest.seed.amount, max: baseInput.farmHarvest.seed.amount },
        baseInput.farmHarvest.seed.chance,
        this.actionMaxBonus,
        0,
        [],
        this.plotInput.item.uniqueName
      );
      this.outputs.push(newSeedOutput);
    }

    // If farmProducts
    if (baseInput.farmProducts) {
      baseInput.farmProducts.product.lootList.lootItems.forEach((loot) => {
        if (loot.item) {
          const newOutput = new PlotOuputItem(
            loot.item,
            this.userSettings,
            aodpPrices,
            aodpHistories,
            aoDataGetter,
            { min: loot.amountMin, max: loot.amountMax },
            loot.chance,
            0,
            1,
            this.farmingModifiers,
            this.plotInput.item.uniqueName
          );
          this.outputs.push(newOutput);
        }
      });
    }

    // If farmGrownItem
    if (baseInput.farmGrownItem) {
      const babyOutput = new PlotOuputItem(
        baseInput,
        this.userSettings,
        aodpPrices,
        aodpHistories,
        aoDataGetter,
        { min: baseInput.farmGrownItem.offspring.amount, max: baseInput.farmGrownItem.offspring.amount },
        baseInput.farmGrownItem.offspring.chance,
        this.actionMaxBonus,
        0,
        [],
        this.plotInput.item.uniqueName
      );
      this.outputs.push(babyOutput);

      if (baseInput.farmGrownItem.item) {
        const grownOutput = new PlotOuputItem(
          baseInput.farmGrownItem.item,
          this.userSettings,
          aodpPrices,
          aodpHistories,
          aoDataGetter,
          { min: 1, max: 1 },
          1,
          0,
          0,
          [],
          this.plotInput.item.uniqueName
        );
        this.outputs.push(grownOutput);
      }
    }

    // If consumption
    if (baseInput.farmConsumption) {
      const possibleFood = foodItems.filter((x) => x.foodCategory === baseInput.farmConsumption?.food.acceptedFood.foodCategory);
      possibleFood.forEach((food) => {
        const isFavorite = food.uniqueName === baseInput.farmConsumption?.food.acceptedFood.favorite;
        let nutritionBonus = baseInput.farmConsumption?.food.acceptedFood.favoriteBonus ?? 0;
        if (isNaN(nutritionBonus)) nutritionBonus = 0;

        const newNutrition = new PlotNutritionItem(
          food,
          isFavorite,
          this.userSettings,
          this.farmDurationSecondsNoPremium,
          food.nutrition ?? 0,
          nutritionBonus,
          baseInput.farmConsumption?.food.secondsPerNutrition ?? 1,
          aodpPrices,
          aodpHistories,
          this.aoDataGetter
        );
        this.nutritionInputs.push(newNutrition);
      });
    }

    this.selectCheapestNutrition();

    // Achievements
    this.achievements = findMatchingAchievements(achievements, baseInput, ['farmraisefame', 'farmharvestfame']);
    this.setUserAchievementsLevels();
  }

  getFocusEfficiencyText(ach: Achievement): string {
    return getFocusEfficiencyText(ach, this.plotInput.item, 'farmingfocuscostreduction');
  }

  selectNutrition(nutrition: PlotNutritionItem): void {
    this.nutritionInputs.forEach((input) => input.setSelected(false));
    nutrition.setSelected(true);
  }

  calculate(resetChosenPrices: boolean = false): void {
    this.plotInput.calculate(resetChosenPrices);
    this.nutritionInputs.forEach((input) => input.calculate(resetChosenPrices));
    this.outputs.forEach((output) => output.calculate(resetChosenPrices));
    this.setCheapestNutrition();
  }

  setCheapestNutrition(): void {
    if (this.nutritionInputs.length === 0) return;

    const validNutrition = this.nutritionInputs.filter((x) => x.chosenPrice > 0);
    if (validNutrition.length === 0) return;

    const cheapestNutrition = validNutrition.reduce((acc, x) => (x.pricePerNutrition < acc.pricePerNutrition ? x : acc), validNutrition[0]);

    this.nutritionInputs.forEach((x) => x.setCheapest(x === cheapestNutrition));
  }

  selectCheapestNutrition(): void {
    const cheapestNutrition = this.nutritionInputs.find((x) => x.isCheapest);
    if (cheapestNutrition) {
      this.selectNutrition(cheapestNutrition);
    }
  }

  setUserAchievementsLevels(): void {
    this.achievements.forEach((ach) => {
      ach.playerLevel = this.userSettings.getUserSpecMap().get(ach.id) ?? 0;
    });
  }
}

// MARK: PlotInputItem
export class PlotInputItem {
  aodpPrice: number = 0;
  npcPrice: number | undefined;
  realAmount: number = 0;
  chosenPrice: number = 0;
  price: number = 0;
  setupFeePercentage: number = 0;
  feesCost: number = 0;
  totalItemValue: number = 0;
  isUsingNpcPrice: boolean = false;

  constructor(
    public item: Item,
    private baseInputAmount: number,
    private userSettings: UserSettingsService,
    private aodpPrices: MarketResponse[],
    private aodpHistories: MarketHistoriesResponse[],
    private aoDataGetter: AoDataGetterService
  ) {}

  calculate(resetChosenPrice: boolean = false): void {
    this.setRealAmount();
    this.setNpcPrice();
    this.setSetupFeePercentage();
    this.setAodpPrice();
    this.setPrice();
    if (resetChosenPrice) {
      this.resetChosenPrice();
    }
    this.setFeesCost();
    this.setTotalItemValue();
  }

  setRealAmount(): void {
    this.realAmount = this.baseInputAmount;
  }

  setNpcPrice(): void {
    const swapTransaction = this.item.craftingRequirements.find((x) => x.swapTransaction);
    this.npcPrice = swapTransaction ? (swapTransaction.silver ?? 0) * (1 - this.userSettings.getUserFarmConfig().globalDiscountPercent / 100) : undefined;
  }
  setPrice(): void {
    if (this.userSettings.getUserFarmConfig().buyFromNpc) {
      if (!!this.npcPrice) {
        this.price = this.npcPrice;
        this.isUsingNpcPrice = true;
        return;
      } else {
        this.price = this.aodpPrice;
        this.isUsingNpcPrice = false;
        return;
      }
    }
    this.price = this.aodpPrice;
    this.isUsingNpcPrice = false;
  }

  setSetupFeePercentage(): void {
    this.setupFeePercentage = this.userSettings.getUserFarmConfig().hasPremium ? SETUP_FEES.normal.value : SETUP_FEES.normal.value;
  }

  setAodpPrice(): void {
    if (this.userSettings.getUserFarmConfig().buyPriceType === PRICE_TYPES.sell) {
      let buyLocationMinMax = this.aoDataGetter.getMinAndMaxPrice(this.item, this.userSettings.getUserFarmConfig().buyLocation, this.aodpPrices);
      this.aodpPrice = buyLocationMinMax.minSellPrice?.sellPriceMin ?? 0;
    } else if (this.userSettings.getUserFarmConfig().buyPriceType === PRICE_TYPES.buy) {
      let buyLocationMinMax = this.aoDataGetter.getMinAndMaxPrice(this.item, this.userSettings.getUserFarmConfig().buyLocation, this.aodpPrices);
      this.aodpPrice = buyLocationMinMax.maxBuyPrice?.buyPriceMax ?? 0;
    } else if (this.userSettings.getUserFarmConfig().buyPriceType === PRICE_TYPES.average) {
      let buyLocationhistory = this.aoDataGetter.getAveragePriceAndAmount(
        this.item,
        this.userSettings.getUserFarmConfig().buyLocation,
        this.aodpHistories,
        undefined,
        this.userSettings.getUserFarmConfig().averageNumberOfDays
      );
      this.aodpPrice = Math.ceil(buyLocationhistory.averagePrice ?? 0);
    }
  }

  setFeesCost(): void {
    if (this.isUsingNpcPrice) {
      this.feesCost = 0;
      return;
    }

    if (this.userSettings.getUserFarmConfig().buyPriceType === PRICE_TYPES.buy) {
      this.feesCost = this.chosenPrice * this.setupFeePercentage * this.realAmount;
      return;
    }
    this.feesCost = 0;
  }

  setTotalItemValue(): void {
    this.totalItemValue = this.realAmount * this.chosenPrice;
  }

  resetChosenPrice(): void {
    this.chosenPrice = this.price;
  }
}

// MARK: PlotNutritionItem
export class PlotNutritionItem {
  aodpPrice: number = 0;
  realAmount: number = 0;
  chosenPrice: number = 0;
  setupFeePercentage: number = 0;
  feesCost: number = 0;
  totalItemValue: number = 0;
  realNutrition: number = 0;
  pricePerNutrition: number = 0;
  isSelected: boolean = false;
  isCheapest: boolean = false;
  realNutritionBonus: number = 0;
  constructor(
    public item: Item,
    public isFavorite: boolean,
    private userSettings: UserSettingsService,
    private growTime: number,
    public nutrition: number,
    private nutritionBonus: number,
    private secondsPerNutrition: number,
    private aodpPrices: MarketResponse[],
    private aodpHistories: MarketHistoriesResponse[],
    private aoDataGetter: AoDataGetterService
  ) {}

  calculate(resetChosenPrice: boolean = false): void {
    this.setRealNutritionBonus();
    this.setRealNutrition();
    this.setRealAmount();
    this.setAodpPrice();
    if (resetChosenPrice) {
      this.resetChosenPrice();
    }
    this.setPricePerNutrition();
    this.setSetupFeePercentage();
    this.setFeesCost();
    this.setTotalItemValue();
  }

  setSelected(isSelected: boolean): void {
    this.isSelected = isSelected;
  }

  setCheapest(isCheapest: boolean): void {
    this.isCheapest = isCheapest;
  }

  setRealNutritionBonus(): void {
    if (this.isFavorite) {
      this.realNutritionBonus = this.nutritionBonus;
      return;
    }
    this.realNutritionBonus = 0;
  }

  setRealNutrition(): void {
    this.realNutrition = this.nutrition * (1 + this.realNutritionBonus);
  }
  setRealAmount(): void {
    this.realAmount = Math.round(this.growTime / this.secondsPerNutrition / this.realNutrition);
  }

  setPricePerNutrition(): void {
    this.pricePerNutrition = this.chosenPrice / this.realNutrition;
  }

  setSetupFeePercentage(): void {
    this.setupFeePercentage = this.userSettings.getUserFarmConfig().hasPremium ? SETUP_FEES.normal.value : SETUP_FEES.normal.value;
  }

  setAodpPrice(): void {
    if (this.userSettings.getUserFarmConfig().buyPriceType === PRICE_TYPES.sell) {
      let buyLocationMinMax = this.aoDataGetter.getMinAndMaxPrice(this.item, this.userSettings.getUserFarmConfig().buyLocation, this.aodpPrices);
      this.aodpPrice = buyLocationMinMax.minSellPrice?.sellPriceMin ?? 0;
    } else if (this.userSettings.getUserFarmConfig().buyPriceType === PRICE_TYPES.buy) {
      let buyLocationMinMax = this.aoDataGetter.getMinAndMaxPrice(this.item, this.userSettings.getUserFarmConfig().buyLocation, this.aodpPrices);
      this.aodpPrice = buyLocationMinMax.maxBuyPrice?.buyPriceMax ?? 0;
    } else if (this.userSettings.getUserFarmConfig().buyPriceType === PRICE_TYPES.average) {
      let buyLocationhistory = this.aoDataGetter.getAveragePriceAndAmount(
        this.item,
        this.userSettings.getUserFarmConfig().buyLocation,
        this.aodpHistories,
        undefined,
        this.userSettings.getUserFarmConfig().averageNumberOfDays
      );
      this.aodpPrice = Math.ceil(buyLocationhistory.averagePrice ?? 0);
    }
  }

  setFeesCost(): void {
    if (this.userSettings.getUserFarmConfig().buyPriceType === PRICE_TYPES.buy) {
      this.feesCost = this.chosenPrice * this.setupFeePercentage * this.realAmount;
      return;
    }
    this.feesCost = 0;
  }

  setTotalItemValue(): void {
    this.totalItemValue = this.realAmount * this.chosenPrice;
  }

  resetChosenPrice(): void {
    this.chosenPrice = this.aodpPrice;
  }
}

// MARK: PlotOutputItem
export class PlotOuputItem {
  aodpPrice: number = 0;
  realAmount: number = 0;
  chosenPrice: number = 0;
  setupFeePercentage: number = 0;
  salesTaxesPercentage: number = 0;
  feesCost: number = 0;
  salesTaxesCost: number = 0;
  totalItemValue: number = 0;
  locationBonus: number = 0;
  constructor(
    public item: Item,
    private userSettings: UserSettingsService,
    private aodpPrices: MarketResponse[],
    private aodpHistories: MarketHistoriesResponse[],
    private aoDataGetter: AoDataGetterService,
    private baseOutputAmount: { min: number; max: number },
    private baseOtputChance: number,
    private activeBonus: number,
    private premiumBonus: number,
    private farmingModifiers: FarmingModifier[],
    private plotInputUniqueName: string
  ) {}

  calculate(resetChosenPrice: boolean = false): void {
    this.setLocationBonus();
    this.setSetupFeePercentage();
    this.setSalesTaxesPercentage();
    this.setAodpPrice();
    this.setRealAmount();
    if (resetChosenPrice) {
      this.resetChosenPrice();
    }
    this.setFeesCost();
    this.setSalesTaxesCost();
    this.setTotalItemValue();
  }

  setSalesTaxesPercentage(): void {
    this.salesTaxesPercentage = this.userSettings.getUserFarmConfig().hasPremium ? SALES_TAXES.premium.value : SALES_TAXES.normal.value;
  }
  setSetupFeePercentage(): void {
    this.setupFeePercentage = this.userSettings.getUserFarmConfig().hasPremium ? SETUP_FEES.normal.value : SETUP_FEES.normal.value;
  }

  setAodpPrice(): void {
    if (this.userSettings.getUserFarmConfig().sellPriceType === PRICE_TYPES.sell) {
      let sellLocationMinMax = this.aoDataGetter.getMinAndMaxPrice(this.item, this.userSettings.getUserFarmConfig().sellLocation, this.aodpPrices);
      this.aodpPrice = sellLocationMinMax.minSellPrice?.sellPriceMin ?? 0;
    } else if (this.userSettings.getUserFarmConfig().sellPriceType === PRICE_TYPES.buy) {
      let sellLocationMinMax = this.aoDataGetter.getMinAndMaxPrice(this.item, this.userSettings.getUserFarmConfig().sellLocation, this.aodpPrices);
      this.aodpPrice = sellLocationMinMax.maxBuyPrice?.buyPriceMax ?? 0;
    } else if (this.userSettings.getUserFarmConfig().sellPriceType === PRICE_TYPES.average) {
      let sellLocationhistory = this.aoDataGetter.getAveragePriceAndAmount(
        this.item,
        this.userSettings.getUserFarmConfig().sellLocation,
        this.aodpHistories,
        undefined,
        this.userSettings.getUserFarmConfig().averageNumberOfDays
      );
      this.aodpPrice = Math.ceil(sellLocationhistory.averagePrice ?? 0);
    }
  }

  setLocationBonus(): void {
    if (this.farmingModifiers.length === 0) {
      this.locationBonus = 0;
      return;
    }

    const config = this.userSettings.getUserFarmConfig();
    let modifier = 0;
    if (config.farmOutlands) {
      modifier =
        this.farmingModifiers
          .find((x) => x.biome === config.farmBiome && x.clusterQuality === config.farmClusterQuality)
          ?.farmingYieldModifier.find((x) => x.uniqueName === this.plotInputUniqueName)?.value ?? 0;
    } else {
      modifier =
        this.farmingModifiers.find((x) => x.location === config.islandLocation)?.farmingYieldModifier.find((x) => x.uniqueName === this.plotInputUniqueName)
          ?.islandValue ?? 0;
    }

    this.locationBonus = modifier;
  }

  setRealAmount(): void {
    const activeBonus = this.userSettings.getUserFarmConfig().useFocus ? this.activeBonus : 0;
    const premiumBonus = this.userSettings.getUserFarmConfig().hasPremium ? this.premiumBonus : 0;
    let result =
      ((this.baseOutputAmount.min + this.baseOutputAmount.max) / 2) * (this.baseOtputChance + activeBonus) * (1 + this.locationBonus) * (1 + premiumBonus);

    this.realAmount = result;
  }

  setFeesCost(): void {
    if (this.userSettings.getUserFarmConfig().sellPriceType === PRICE_TYPES.sell) {
      this.feesCost = this.chosenPrice * this.salesTaxesPercentage * this.realAmount;
      return;
    }
    this.feesCost = 0;
  }

  setSalesTaxesCost(): void {
    this.salesTaxesCost = this.chosenPrice * this.setupFeePercentage * this.realAmount;
  }

  setTotalItemValue(): void {
    this.totalItemValue = this.realAmount * this.chosenPrice;
  }

  resetChosenPrice(): void {
    this.chosenPrice = this.aodpPrice;
  }
}

// MARK: FarmConfiguration
export class FarmConfiguration {
  islandLocation: Location = LOCATIONS.LymhurstCity;
  hasPremium: boolean = true;
  useFocus: boolean = true;
  buyFromNpc: boolean = false;

  hideMissingPrices: boolean = false;
  hideUnusedFood: boolean = false;
  hideFameData: boolean = false;

  selectedPlotKinds: string[] = [];

  farmOutlands: boolean = false;
  farmBiome: Biome = Biome.FOREST;
  farmClusterQuality: ClusterQuality = ClusterQuality.Q1;

  buyLocation: Location = LOCATIONS.Caerleon;
  sellLocation: Location = LOCATIONS.Caerleon;
  buyPriceType: PriceType = PRICE_TYPES.sell;
  sellPriceType: PriceType = PRICE_TYPES.sell;
  averageNumberOfDays: number = 7;

  globalDiscountPercent: number = 0;

  static fromJson(json: string): FarmConfiguration {
    const plainObject = JSON.parse(json);

    const result = Object.assign(new FarmConfiguration(), plainObject);

    // Convert enums
    result.farmBiome = toBiome(result.farmBiome);
    result.farmClusterQuality = toClusterQuality(result.farmClusterQuality);

    // Convert location objects
    result.islandLocation = getLocation(Number(result.islandLocation.index));
    result.buyLocation = getLocation(Number(result.buyLocation.index));
    result.sellLocation = getLocation(Number(result.sellLocation.index));

    // Convert price type objects
    result.buyPriceType = getPriceTypeByName(result.buyPriceType.name);
    result.sellPriceType = getPriceTypeByName(result.sellPriceType.name);

    // No need to convert primitive types (booleans and numbers)
    // as they are handled automatically by JSON.parse

    return result;
  }
}

// MARK: Farming Modifier
export enum Biome {
  SWAMP = 'Swamp',
  MOUNTAIN = 'Mountain',
  HIGHLAND = 'Highland',
  STEPPE = 'Steppe',
  FOREST = 'Forest',
}

export function toBiome(value: string): Biome | undefined {
  if (!value) return undefined;

  // Case insensitive comparison
  const normalized = value.toUpperCase();
  const match = Object.entries(Biome).find(([_, enumValue]) => enumValue.toUpperCase() === normalized);
  return match ? Biome[match[0] as keyof typeof Biome] : undefined;
}

export enum ClusterQuality {
  Q1 = 'Q1',
  Q2 = 'Q2',
  Q3 = 'Q3',
  Q4 = 'Q4',
  Q5 = 'Q5',
  Q6 = 'Q6',
}

export function toClusterQuality(value: string): ClusterQuality | undefined {
  if (!value) return undefined;

  // Case insensitive comparison
  const normalized = value.toUpperCase();
  const match = Object.entries(ClusterQuality).find(([_, enumValue]) => enumValue.toUpperCase() === normalized);
  return match ? ClusterQuality[match[0] as keyof typeof ClusterQuality] : undefined;
}

export enum Continent {
  ROYAL = 'Royal',
  OUTLANDS = 'Outlands',
}

export function toContinent(value: string): Continent | undefined {
  if (!value) return undefined;

  // Case insensitive comparison
  const normalized = value.toUpperCase();
  const match = Object.entries(Continent).find(([_, enumValue]) => enumValue.toUpperCase() === normalized);
  return match ? Continent[match[0] as keyof typeof Continent] : undefined;
}

export interface FarmingYieldModifier {
  item: Item;
  uniqueName: string;
  value: number;
  islandValue: number;
}

export interface FarmingModifier {
  clusterId?: string;
  location?: Location;
  continent?: Continent;
  biome?: Biome;
  clusterQuality?: ClusterQuality;
  farmingYieldModifier: FarmingYieldModifier[];
}
